import type { AutomaticUpdatesView } from '@api/devices/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAutomaticUpdates, setAutomaticUpdates } from '../lib/api';
import { useScope } from '../scope/scope';
import { Skeleton } from '../ui/misc';
import { useToast } from '../ui/toast';

const ON = 'New WITTCO agent versions install themselves on this customer’s computers, checked against WITTCO’s release signature, and roll back if one doesn’t start.';
const OFF = 'Paused: this customer’s computers stay on the agent version they run until someone updates them with a security key.';

const updatesKey = (mspId: string, organizationId: string) => ['scope', mspId, 'automatic-updates', organizationId];

export function useAutomaticUpdates(organizationId: string) {
  const { mspId } = useScope();
  return useQuery({ queryKey: updatesKey(mspId, organizationId), queryFn: ({ signal }) => getAutomaticUpdates(mspId, organizationId, signal) });
}

/** A customer's automatic agent updates (WRMM-87): owners pause or resume them here, everyone else sees which. */
export function AutomaticUpdatesSetting({ organizationId }: { organizationId: string }) {
  const { mspId } = useScope();
  const setting = useAutomaticUpdates(organizationId);
  const client = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: (enabled: boolean) => setAutomaticUpdates(mspId, organizationId, enabled),
    onSuccess: view => { client.setQueryData<AutomaticUpdatesView>(updatesKey(mspId, organizationId), view); toast({ title: view.enabled ? 'Automatic updates on' : 'Automatic updates paused', body: view.enabled ? ON : OFF }); },
    onError: () => toast({ tone: 'warning', title: 'The setting wasn’t saved', body: 'Only an owner can change it. Try again.' }),
  });
  if (setting.isPending) return <Skeleton className="h-10 w-full" />;
  if (setting.isError) return <p className="text-[13px] text-ink-3">The agent update setting couldn’t be loaded.</p>;
  const { enabled, canChange } = setting.data;
  if (!canChange) return <p className="text-[13px] text-ink-2">{enabled ? ON : OFF} <span className="text-ink-3">Only an owner can change this.</span></p>;
  return (
    <label className="flex items-start gap-2 text-[13.5px]">
      <input type="checkbox" className="mt-1" checked={enabled} disabled={save.isPending} onChange={e => save.mutate(e.target.checked)} />
      <span>Update the agent automatically <span className="block text-[12.5px] text-ink-3">{enabled ? ON : OFF}</span></span>
    </label>
  );
}
